import React, { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

const COUNT_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);

/**
 * How many blog posts show on the home page — separate dropdowns for
 * desktop and mobile (1–15 each), since a count that looks right on a
 * wide desktop grid can be way too long/short scrolling on a phone.
 * Persists to the shared /api/settings/theme document
 * (home_posts_count_desktop / home_posts_count_mobile), read by Home.jsx.
 */
export default function AdminHomePostCountPanel() {
  const { theme, refresh } = useTheme();
  const [desktopCount, setDesktopCount] = useState(7);
  const [mobileCount, setMobileCount] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!theme) return;
    setDesktopCount(theme.home_posts_count_desktop || 7);
    setMobileCount(theme.home_posts_count_mobile || 7);
    setLoading(false);
  }, [theme]);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put("/settings/theme", {
        home_posts_count_desktop: desktopCount,
        home_posts_count_mobile: mobileCount,
      });
      await refresh();
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section
        className="mb-8 rounded-md border border-black/10 bg-white p-5"
        data-testid="admin-home-post-count-panel"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </section>
    );
  }

  return (
    <section
      className="mb-8 rounded-md border border-black/10 bg-white p-5"
      data-testid="admin-home-post-count-panel"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Homepage post count</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            How many posts show on the home page (1 featured + the rest as
            cards) — set independently for desktop and mobile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash ? (
            <span
              className="inline-flex items-center gap-1 text-sm text-emerald-700"
              data-testid="admin-home-post-count-saved-flash"
            >
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
            data-testid="admin-home-post-count-save"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save
          </button>
        </div>
      </div>

      {err ? (
        <div
          className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
          data-testid="admin-home-post-count-error"
        >
          {err}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-6">
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
            Desktop
          </span>
          <select
            value={desktopCount}
            onChange={(e) => setDesktopCount(Number(e.target.value))}
            className="mt-1 block w-28 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            data-testid="admin-home-post-count-desktop"
          >
            {COUNT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} post{n === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
            Mobile
          </span>
          <select
            value={mobileCount}
            onChange={(e) => setMobileCount(Number(e.target.value))}
            className="mt-1 block w-28 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            data-testid="admin-home-post-count-mobile"
          >
            {COUNT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} post{n === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}
